import {projectId, isDemoProject} from '-/firebase';

const isDemo = isDemoProject(projectId);
const isJRFD = projectId === 'jrfd-log';
const isSicamous = projectId === 'sicamousfd';
const isNorthShore = projectId === 'northshorefd-6970a';

// North Shore is the RDCK (Regional District of Central Kootenay) tenant. Demo is included so
// RDCK-specific UI (payroll report, Viera matrix labels, Homewood Health tracking) stays
// visible in dev/demo. Use this throughout the codebase for RDCK-only behaviour.
//
// DEPRECATED in favour of the hub link below. A department now knows which
// regional hub it belongs to (`settings/general.hub`, #1602), so "is this RDCK?"
// should be "is this department in the RDCK hub?" — which makes onboarding a
// second RDCK department a registry + tenants.js change rather than a code
// change here.
//
// Migrating the remaining `isRdck` importers is NOT a mechanical swap, because
// this is a module-scope const and the hub link arrives asynchronously with
// settings. `pages/matrix/Qualification.js` calls
// `createRenderTrainingCell({isRdck, …})` at module scope — it captures the
// value at import time, long before settings load, so a hub-derived value would
// read `false` there forever. Each call site has to move to a render-time read
// (hasFeature / useContext) first. See #1602.
export const isRdck = isNorthShore || isDemo;

export const RDCK_HUB_PROJECT_ID = 'rdck-4de86';

// The regional hub this department belongs to, or null. Mirrored into
// `settings/general.hub` by the api once BOTH sides of the link agree (the hub's
// registry and the tenant's `hub:` in common/tenants.js) — see
// api/src/routes/hub/_projects.js.
//
// Only meaningful after settings load. Read it at render time, never at module
// scope.
export const getHubProjectId = () => _settings?.hub?.projectId || null;

export const isInHub = hubProjectId => Boolean(hubProjectId) && getHubProjectId() === hubProjectId;

// A value may be a function, evaluated per call — that's what lets a flag depend
// on settings (and therefore on the hub link) while the static project-id flags
// stay plain booleans.
const features = {
    actingRoles: isSicamous,
    importIncidentAndTrainingRecordsFromIAR: isJRFD,
    rdcoPayrollReport: isJRFD || isDemo,
    // The Workzoom page: JRFD members key their own hours into RDCO's Workzoom
    // app every bi-weekly pay period, and this lists what Ember Tracking has for
    // them in the shape the app asks for. Demo included so it can be tried in
    // the emulator.
    workzoom: isJRFD || isDemo,
    // First flag off the hub link. `|| isRdck` keeps North Shore and demo working
    // before/without the mirror, so this is additive: a second department joining
    // the RDCK hub picks the report up with no release.
    rdckPayrollReport: () => isInHub(RDCK_HUB_PROJECT_ID) || isRdck,
    // The Onboarding page (docs/architecture/onboarding-requests.md).
    // Not a Settings → Features toggle: the operator switches it on for a
    // department being set up, through the api, and it lives at the top of
    // settings/general where the department's own saves can't reach it.
    onboardingRequests: () => _settings?.onboardingRequests?.enabled === true
};

// Settings-based features with defaults (used before settings load)
export const settingsFeatures = {
    quizzes: true,
    dutyShifts: true,
    preplans: true,
    files: true,
    publicEducation: true,
    importPaperPretrips: false,
    deployments: true,
    fuel: true,
    calendar: true,
    lessonPlans: true,
    requirements: true,
    qualificationMatrix: true,
    payroll: true,
    trainingStandards: true,
    emcrForms: false,
    // #1481/#1482/#1568. On by default like every other module: the flag only
    // makes the surface available. Nothing is collected until a department
    // creates a record, and reading someone else's needs the separate hr.*
    // permission that no role grants — so "on" costs a department nothing until
    // they opt into it by assigning that permission. Turn it off in Settings →
    // Features to hide the tab and licence fields entirely.
    hr: true,
    // Off by default, unlike the modules above: a permit list only exists where
    // a regional district hands one to the department (RDCO sends JRFD theirs
    // weekly through burning season), so everywhere else it would be an empty
    // page. A department that gets one turns it on in Settings → Features.
    burnPermits: isJRFD || isDemo,
    // Ask (#2204): plain-English questions answered from the department's own
    // records. Off by default everywhere, demo included — it calls the
    // department's AI (Vertex) with the question, so a department opts in from
    // Settings → Features. Off only hides it from the menu: /ask stays
    // reachable by URL (Router.js) so it can be tried before it's switched on.
    ask: false,
    // OH&S meetings (#2295): a page for recording the monthly joint health and
    // safety meeting, and the Hub's view of every hall's. Off by default — only
    // departments whose regional district asks for it use it.
    ohs: false
};

let _settings = null;

export const setFeatureSettings = settings => {
    _settings = settings;
};

export const hasFeature = feature => {
    // Check hardcoded features first
    if (feature in features) {
        const value = features[feature];
        return (typeof value === 'function' ? value() : value) === true;
    }
    // A feature the department's regional hub turned on for all its departments
    // (hub Settings → Features, #2295). The api mirrors it into the `hub` link
    // marker, which only it can write, so a hub can force a feature on but
    // never off, and unlinking from the hub hands the choice back.
    if (_settings?.hub?.features?.[feature] === true) {
        return true;
    }
    // Check settings-based features
    const settingsFeatureValue = _settings?.features?.[feature];
    if (settingsFeatureValue !== undefined) {
        return settingsFeatureValue === true;
    }
    // Fall back to defaults
    if (feature in settingsFeatures) {
        return settingsFeatures[feature];
    }
    return false;
};
